// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

/**
 * Bounded power-flow neighbourhood for a single asset.
 *
 * Given the flat twin graph (`entities` + `relationships`) and a focus entity,
 * this walks the power-flow chain a fixed number of hops in each direction and
 * returns a small, render-ready view model: what feeds the focus (upstream) and
 * what the focus feeds (downstream). It is the data behind `InlineEquipmentView`.
 *
 * Direction is not re-derived here — every relationship is passed through
 * `normalizeFlowRelationship` (see `flowRelationships.ts`), which already flips the
 * ontology's inverse types (`fedBy`, `suppliedBy`, `primarySource`,
 * `backupSource`) so that `source` is always the supplier and `target` the
 * consumer, and drops non-power links (`connectedTo`, containment, telemetry).
 * Getting that backwards would silently reverse the chain, so it lives in one
 * tested place.
 *
 * Feeds fan out, so each level is capped: at most `maxPerLevel` nodes are kept
 * and the remainder is reported as `overflow` for the UI to show as "+N".
 */

import type { Entity, Relationship } from '../types/digitalTwin';
import { normalizeFlowRelationship } from './flowRelationships';
import { assetName } from './entityLabels';
import {
  aggregateChildStatus,
  buildTelemetryChildIndex,
  categorizeStatus,
  getEntityCode,
  getEntityDisplayType,
  getStatusToken,
  type StatusToken,
} from './entityHelpers';

/** A single equipment chip in the flow view. */
export interface EquipmentNode {
  id: string;
  label: string;
  /** Ontology display type, e.g. "Transformer". */
  typeLabel: string;
  /** Short uppercase code for the monospace badge, e.g. "XFMR". */
  code: string;
  status: StatusToken;
}

/** One hop's worth of neighbours, with a count of what was capped off. */
export interface EquipmentFlowLevel {
  nodes: EquipmentNode[];
  /** Renderable neighbours at this level beyond `maxPerLevel` (drives "+N"). */
  overflow: number;
}

export interface EquipmentFlow {
  /** The asset the view is centred on, or `null` when it isn't in the graph. */
  focus: EquipmentNode | null;
  /** Feeders. Index 0 is one hop up, index 1 is two hops up, … */
  upstream: EquipmentFlowLevel[];
  /** Downstream loads. Index 0 is one hop down, index 1 is two hops down, … */
  downstream: EquipmentFlowLevel[];
}

export interface BuildEquipmentFlowOptions {
  /** How many hops to walk in each direction. Default 2. */
  maxDepth?: number;
  /** Max nodes rendered per level before the overflow chip. Default 2. */
  maxPerLevel?: number;
}

const DEFAULT_MAX_DEPTH = 2;
const DEFAULT_MAX_PER_LEVEL = 2;

/** Directed flow adjacency: `out` = feeds (downstream), `in` = fed-by (upstream). */
interface FlowAdjacency {
  out: Map<string, Set<string>>;
  in: Map<string, Set<string>>;
}

const addEdge = (adj: Map<string, Set<string>>, from: string, to: string): void => {
  const set = adj.get(from);
  if (set) set.add(to);
  else adj.set(from, new Set([to]));
};

/**
 * Build the downstream-pointing adjacency once. Non-flow relationships and any
 * relationship that doesn't normalise to a power-flow edge are ignored.
 */
const buildFlowAdjacency = (relationships: Relationship[]): FlowAdjacency => {
  const out = new Map<string, Set<string>>();
  const incoming = new Map<string, Set<string>>();
  for (const rel of relationships) {
    const flow = normalizeFlowRelationship(rel);
    if (!flow) continue;
    // flow.source feeds flow.target.
    addEdge(out, flow.source, flow.target);
    addEdge(incoming, flow.target, flow.source);
  }
  return { out, in: incoming };
};

/**
 * Walk `adjacency` outward from `focusId` up to `maxDepth` hops, capping each
 * level to `maxPerLevel` renderable nodes. `visited` is shared across the whole
 * traversal (both directions and the focus) so no asset is ever shown twice —
 * the closest occurrence wins.
 */
const walkLevels = (
  focusId: string,
  adjacency: Map<string, Set<string>>,
  visited: Set<string>,
  toNode: (_id: string) => EquipmentNode | null,
  maxDepth: number,
  maxPerLevel: number
): EquipmentFlowLevel[] => {
  const levels: EquipmentFlowLevel[] = [];
  let frontier: string[] = [focusId];

  for (let depth = 0; depth < maxDepth; depth++) {
    // Union of the frontier's neighbours, keeping only unseen, renderable nodes.
    const candidates: EquipmentNode[] = [];
    const seenThisLevel = new Set<string>();
    for (const nodeId of frontier) {
      for (const neighbourId of Array.from(adjacency.get(nodeId) ?? [])) {
        if (visited.has(neighbourId) || seenThisLevel.has(neighbourId)) continue;
        const node = toNode(neighbourId);
        if (!node) continue; // endpoint not an entity we can render
        seenThisLevel.add(neighbourId);
        candidates.push(node);
      }
    }

    if (candidates.length === 0) break;

    // Deterministic order: label, then id. The details API cursor-paginates, so
    // relationship array order is not stable between loads.
    candidates.sort((a, b) => a.label.localeCompare(b.label) || a.id.localeCompare(b.id));

    // Mark every candidate visited — the hidden ones are "overflow" at this
    // level, not deferred to a farther one.
    for (const node of candidates) visited.add(node.id);

    const nodes = candidates.slice(0, maxPerLevel);
    levels.push({ nodes, overflow: candidates.length - nodes.length });

    // Only walk on from the nodes actually shown: a level-2 asset whose feeder
    // is hidden behind the overflow chip would otherwise appear orphaned.
    frontier = nodes.map(n => n.id);
  }

  return levels;
};

/**
 * Assemble the bounded upstream/downstream flow view for one asset.
 *
 * Returns `focus: null` (and empty levels) when `focusId` isn't present in
 * `entities`, so callers can render nothing without a separate existence check.
 */
export const buildEquipmentFlow = (
  focusId: string,
  entities: Entity[],
  relationships: Relationship[],
  options: BuildEquipmentFlowOptions = {}
): EquipmentFlow => {
  const maxDepth = options.maxDepth ?? DEFAULT_MAX_DEPTH;
  const maxPerLevel = options.maxPerLevel ?? DEFAULT_MAX_PER_LEVEL;

  const entityMap = new Map(entities.map(e => [e.id, e]));
  const childIndex = buildTelemetryChildIndex(relationships);

  const toNode = (id: string): EquipmentNode | null => {
    const entity = entityMap.get(id);
    if (!entity) return null;
    const statusStr = aggregateChildStatus(entity, entities, relationships, entityMap, childIndex);
    return {
      id,
      label: assetName(entity, id),
      typeLabel: getEntityDisplayType(entity.type),
      code: getEntityCode(entity.type),
      status: getStatusToken(categorizeStatus(statusStr)),
    };
  };

  const focus = focusId ? toNode(focusId) : null;
  if (!focus) {
    return { focus: null, upstream: [], downstream: [] };
  }

  const { out, in: incoming } = buildFlowAdjacency(relationships);

  // Shared across both directions so a cyclic link can't render an asset twice.
  const visited = new Set<string>([focus.id]);
  const upstream = walkLevels(focus.id, incoming, visited, toNode, maxDepth, maxPerLevel);
  const downstream = walkLevels(focus.id, out, visited, toNode, maxDepth, maxPerLevel);

  return { focus, upstream, downstream };
};
